import { EllipsisIcon, XIcon, type LucideIcon } from "lucide-react"
import { isValidElement, useState, type ReactNode } from "react"
import { Button } from "@/primitives/button.tsx"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/primitives/dropdown-menu.tsx"
import { cn } from "@/primitives/utils"
import { MenuConfirmationContext } from "./menu-confirmation.ts"

/*
 * What a row lets a Member do to it, out of sight until they reach for it: a ⋯ menu, or a quiet ×
 * for the one thing a row offers, each shown while the pointer is over the row, focus is inside
 * it, or its menu is open. Nothing in a row is red until the Member confirms; the confirmation
 * dialog itself is the page's (a DestructiveConfirmation or a Confirmation), so the words of what
 * goes live with the rest of those words.
 *
 * A row reveals them by being a `group/row`, as a table row is. An item that asks first closes the
 * menu as any item does; its dialog is drawn beside the menu, so it outlives it
 * (`menu-confirmation.ts`).
 */

/** One entry in a row's ⋯ menu. */
export type RowAction =
  | { label: string; icon: LucideIcon; onSelect: () => void }
  /** A submenu of choices, such as the groups a Member can be added to. */
  | {
      kind: "submenu"
      label: string
      icon: LucideIcon
      items: readonly { label: string; glyph?: ReactNode; onSelect: () => void }[]
    }
  /** A menu item that asks first, such as "Make Admin…": a Confirmation with `trigger="menu-item"`. */
  | { kind: "confirmation"; confirmation: ReactNode }
  | { kind: "separator" }
  /**
   * Something that removes, such as "Remove Status…": a DestructiveConfirmation with
   * `trigger="menu-item"`. Destructive entries always come last, after a separator.
   */
  | { kind: "destructive"; confirmation: ReactNode }

const revealed = {
  hover:
    "opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 group-data-highlighted/row:opacity-100 focus-visible:opacity-100 aria-expanded:opacity-100 data-popup-open:opacity-100",
  always: "",
} as const

/**
 * A row's ⋯ menu. `reveal` is `always` where the menu is the page's own rather than one row's among
 * many, as on a ticket or the Connections card. `label` names the trigger for a screen reader, as
 * "Actions for sean@netlife.ai".
 */
export function RowActions({
  items,
  label = "More actions",
  reveal = "hover",
}: {
  items: readonly RowAction[]
  label?: string
  reveal?: keyof typeof revealed
}) {
  const isDestructive = (item: RowAction) => "kind" in item && item.kind === "destructive"
  const destructive = items.filter(isDestructive)
  const rest = items.filter((item) => !isDestructive(item))
  // The confirmation being asked: its place in `items`, and what it is, since a change it makes
  // can put another in that place ("Make Admin…" becomes "Remove Admin…"), which must stay shut.
  const [asking, setAsking] = useState<{ index: number; type: unknown }>()
  const hosting = (item: RowAction) =>
    ({
      part: "item",
      ask: () => {
        const confirmation = confirmationOf(item)
        setAsking({ index: items.indexOf(item), type: typeOf(confirmation) })
      },
    }) as const
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={label}
              className={cn("transition", revealed[reveal])}
            />
          }
        >
          <EllipsisIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {rest.map((item, index) => (
            <MenuConfirmationContext key={index} value={hosting(item)}>
              <ActionEntry item={item} />
            </MenuConfirmationContext>
          ))}
          {destructive.length > 0 && rest.length > 0 && <DropdownMenuSeparator />}
          {destructive.map((item, index) => (
            <MenuConfirmationContext key={index} value={hosting(item)}>
              {confirmationOf(item)}
            </MenuConfirmationContext>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Each confirmation's dialog, beside the menu, so it stays while the menu closes. */}
      {items.map((item, index) => {
        const confirmation = confirmationOf(item)
        if (confirmation === undefined) return null
        const open = asking?.index === index && asking.type === typeOf(confirmation)
        return (
          <MenuConfirmationContext
            key={index}
            value={{
              part: "dialog",
              open,
              onOpenChange: (next) => {
                if (!next) setAsking(undefined)
              },
            }}
          >
            {confirmation}
          </MenuConfirmationContext>
        )
      })}
    </>
  )
}

/** An entry's confirmation, when it asks first. */
const confirmationOf = (item: RowAction): ReactNode =>
  "confirmation" in item ? item.confirmation : undefined

/** What a confirmation is, as React tells two elements apart: its component. */
const typeOf = (node: ReactNode) => (isValidElement(node) ? node.type : undefined)

function ActionEntry({ item }: { item: RowAction }) {
  if (!("kind" in item))
    return (
      <DropdownMenuItem onClick={item.onSelect}>
        <item.icon />
        {item.label}
      </DropdownMenuItem>
    )
  switch (item.kind) {
    case "separator":
      return <DropdownMenuSeparator />
    case "submenu":
      return (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <item.icon />
            {item.label}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {item.items.map((choice) => (
              <DropdownMenuItem key={choice.label} onClick={choice.onSelect}>
                {choice.glyph}
                {choice.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )
    case "confirmation":
    case "destructive":
      return item.confirmation
  }
}

/**
 * A row's one way to take something out of a list: a muted × that turns red on hover. With a
 * `confirmation` (a DestructiveConfirmation with `trigger="row-x"`, which draws this same ×) it
 * asks first; with `onRemove` it removes at once, for something as easily put back. `label` names
 * what goes, for a screen reader: "Remove sean@netlife.ai from Exec".
 */
export function RowRemove(
  props: { label: string; onRemove: () => void } | { confirmation: ReactNode },
) {
  if ("confirmation" in props)
    return (
      <span
        className={cn(
          "inline-flex transition-opacity duration-100 ease-snappy has-data-popup-open:opacity-100 has-focus-visible:opacity-100",
          revealed.hover,
        )}
      >
        {props.confirmation}
      </span>
    )
  return (
    <Button
      variant="destructive-ghost"
      size="icon-xs"
      aria-label={props.label}
      onClick={props.onRemove}
      className={cn("transition", revealed.hover)}
    >
      <XIcon />
    </Button>
  )
}
